import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// Strip trailing slash to prevent double-slash bug (//api/auth/login → SPA HTML response)
const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper function to sync language from user to localStorage
// Only syncs on first login, respects manual changes during session
const syncUserLanguage = (userData, isInitialLogin = false) => {
  if (userData?.language) {
    const currentLang = localStorage.getItem('alphabit_language');
    const sessionLangOverride = sessionStorage.getItem('alphabit_language_manual');
    
    // If user manually changed language during this session, don't override
    if (sessionLangOverride) {
      return;
    }
    
    // Only sync if no language set or on initial login
    if (!currentLang || isInitialLogin) {
      localStorage.setItem('alphabit_language', userData.language);
      // Dispatch event to notify LanguageContext
      window.dispatchEvent(new CustomEvent('languageSync', { detail: userData.language }));
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('alphabit_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const storedToken = localStorage.getItem('alphabit_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      const userData = response.data;
      setUser(userData);
      setToken(storedToken);
      // Sync language from user profile (not initial login - just session restore)
      syncUserLanguage(userData, false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) {
      payload.totp_code = totpCode;
    }
    const response = await axios.post(`${API_URL}/api/auth/login`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('alphabit_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Sync language from user profile on initial login
    syncUserLanguage(userData, true);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('alphabit_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Sync language from user profile (already set during registration)
    syncUserLanguage(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('alphabit_token');
    setToken(null);
    setUser(null);
  };

  // Create api instance that updates with token changes
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 30000, // 30s timeout — prevents infinite spinner on hung requests (e.g. mobile 4G packet loss)
    });
    
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('alphabit_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, api }}>
      {children}
    </AuthContext.Provider>
  );
};
